
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface CourseTypeChartProps {
  dateRange: { from: Date | undefined; to: Date | undefined };
}

export function CourseTypeChart({ dateRange }: CourseTypeChartProps) {
  const endDate = dateRange.to || new Date();
  const startDate = dateRange.from || new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate());
  
  const { data: courseCertificates, isLoading } = useQuery({
    queryKey: ['courseTypeCertificates', startDate, endDate],
    queryFn: async () => {
      // Join certificates with course_offerings to get course type information
      const { data, error } = await supabase
        .from('certificates')
        .select(`
          id,
          course_id,
          course_offerings!certificates_course_id_fkey (
            course_type,
            course_name
          )
        `)
        .gte('issued_at', startDate.toISOString())
        .lte('issued_at', endDate.toISOString());
      
      if (error) throw error;
      return data;
    },
  });
  
  // Process the data for the chart
  const chartData = React.useMemo(() => {
    if (!courseCertificates) return [];
    
    const courseTypeMap: Record<string, number> = {};
    
    courseCertificates.forEach(cert => {
      // The certificate has a course with type info
      const courseType = cert.course_offerings?.course_type || 'Unknown';
      courseTypeMap[courseType] = (courseTypeMap[courseType] || 0) + 1;
    });
    
    // Convert the map to an array of objects
    return Object.entries(courseTypeMap)
      .map(([courseType, count]) => ({ courseType, count }));
  }, [courseCertificates]);
  
  if (isLoading) {
    return <div>Loading chart data...</div>;
  }
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="courseType" />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend />
        <Bar dataKey="count" name="Number of Certificates" fill="#00dd00" />
      </BarChart>
    </ResponsiveContainer>
  );
}
